import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import { 
  Grid, 
  CircularProgress, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  FormControl, 
  FormLabel,
  Card,
  Box,
} from '@mui/material';
import { MDBox } from 'components/MDBox';
import { MDButton } from 'components/MDButton';
import { MDInput } from 'components/MDInput';
import { MDTypography } from 'components/MDTypography';
import { sendVOB, getSteerageById, VOBData, SteerageWithPolicy } from '../../services/vobService';
import { VobAuthCodeList } from 'components/VobAuthCodeList';

// Validation regex patterns
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const FAX_REGEX = /^\+1[0-9]+$/;
const STEERAGE_ID_REGEX = /^[0-9]{2,7}$/;

// Debounce function
const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): Promise<ReturnType<F>> => {
    if (timeout) {
      clearTimeout(timeout);
    }

    return new Promise(resolve => {
      timeout = setTimeout(() => {
        resolve(func(...args));
      }, waitFor);
    });
  };
};

const VOBForm: React.FC = () => {
  // Contact method state
  const [contactMethod, setContactMethod] = useState<'email' | 'fax'>('fax');

  // Form state
  const [formData, setFormData] = useState<VOBData>({
    steerageId: 0,
    providerName: '',
    providerEmail: '',
    faxNumber: '',
    comments: '',
  });

  // Steerage input state
  const [steerageIdInput, setSteerageIdInput] = useState('');
  
  // Steerage data state
  const [steerageData, setSteerageData] = useState<SteerageWithPolicy | null>(null);
  const [loadingSteerage, setLoadingSteerage] = useState(false);
  const [steerageError, setSteerageError] = useState<string | null>(null);

  // Form validation state
  const [errors, setErrors] = useState({
    steerageId: false,
    providerEmail: false,
    faxNumber: false,
  });

  // Form submission state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Debounced function to fetch steerage
  const debouncedFetchSteerage = useCallback(
    debounce(async (steerageId: string) => {
      if (!steerageId.trim() || !STEERAGE_ID_REGEX.test(steerageId.trim())) {
        setSteerageData(null);
        setSteerageError(null);
        return;
      }
      
      setLoadingSteerage(true);
      setSteerageError(null);
      try {
        const fetchedSteerage = await getSteerageById(steerageId.trim());
        if (fetchedSteerage) {
          setSteerageData(fetchedSteerage);
          setFormData(prev => ({ 
            ...prev, 
            steerageId: parseInt(fetchedSteerage.id),
            providerName: fetchedSteerage.providerName || prev.providerName,
          }));
          setErrors(prev => ({ ...prev, steerageId: false }));
        } else {
          setSteerageData(null);
          setSteerageError('Steerage not found. Please check the ID and try again.');
          setFormData(prev => ({ ...prev, steerageId: 0, providerName: '' }));
        }
      } catch (err) {
        console.error('Error fetching steerage:', err);
        setSteerageData(null);
        setSteerageError('Error fetching steerage data. Please try again.');
        setFormData(prev => ({ ...prev, steerageId: 0, providerName: '' }));
      } finally {
        setLoadingSteerage(false);
      }
    }, 500),
    []
  );

  // Effect to fetch steerage when steerage ID changes
  useEffect(() => {
    if (steerageIdInput) {
      debouncedFetchSteerage(steerageIdInput);
    } else {
      setSteerageData(null);
      setSteerageError(null);
    }
  }, [steerageIdInput, debouncedFetchSteerage]);

  // Handle steerage ID input changes
  const handleSteerageIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Only allow numeric input
    if (value === '' || /^[0-9]{0,7}$/.test(value)) {
      setSteerageIdInput(value);
      
      // Clear errors when user types
      if (errors.steerageId) {
        setErrors(prev => ({ ...prev, steerageId: false }));
      }
    }
  };

  // Handle other input changes
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear errors when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  // Handle contact method change
  const handleContactMethodChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContactMethod(e.target.value as 'email' | 'fax');
    
    // Clear errors for the fields when switching
    setErrors((prev) => ({
      ...prev,
      providerEmail: false,
      faxNumber: false,
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = {
      steerageId: formData.steerageId === 0 || !steerageData,
      providerEmail: contactMethod === 'email' 
        ? !formData.providerEmail?.trim() || !EMAIL_REGEX.test(formData.providerEmail || '')
        : false,
      faxNumber: contactMethod === 'fax'
        ? !formData.faxNumber?.trim() || !FAX_REGEX.test(formData.faxNumber || '')
        : false,
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare data based on selected contact method
      const submissionData: VOBData = {
        ...formData,
        // Only include the field for the selected contact method
        providerEmail: contactMethod === 'email' ? formData.providerEmail : undefined,
        faxNumber: contactMethod === 'fax' ? formData.faxNumber : undefined,
      };

      const response = await sendVOB(submissionData);
      if (response.success) {
        setSuccess(true);
        setSuccessMessage(response.message);
      } else {
        setError(response.message || 'Failed to send VOB. Please try again.');
      }
    } catch (err) {
      const errorMessage = typeof err === 'string' ? err : 'An error occurred while sending the VOB.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset form for a new VOB
  const handleNewVOB = () => {
    setFormData({
      steerageId: 0,
      providerName: '',
      providerEmail: '',
      faxNumber: '',
      comments: '',
    });
    setSteerageIdInput('');
    setSteerageData(null);
    setSteerageError(null);
    setContactMethod('fax');
    setSuccessMessage('');
    setSuccess(false);
    setError(null);
  };

  // Check if form is valid for enabling the submit button
  const isFormValid = (): boolean => {
    return (
      formData.steerageId !== 0 &&
      !!steerageData &&
      ((contactMethod === 'email' && !!formData.providerEmail?.trim() && EMAIL_REGEX.test(formData.providerEmail || '')) ||
       (contactMethod === 'fax' && !!formData.faxNumber?.trim() && FAX_REGEX.test(formData.faxNumber || '')))
    );
  };

  return (
    <MDBox component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Steerage ID */}
        <Grid item xs={12}>
          <MDInput
            type="text"
            label="Steerage ID"
            name="steerageId"
            value={steerageIdInput}
            onChange={handleSteerageIdChange}
            error={errors.steerageId}
            fullWidth
            required
            disabled={loading || success}
            helperText="Enter 2-7 digit steerage ID"
          />
          {errors.steerageId && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              Valid Steerage ID is required
            </MDTypography>
          )}
          {steerageError && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              {steerageError}
            </MDTypography>
          )}
          {loadingSteerage && (
            <MDTypography variant="caption" color="info" fontWeight="light" display="flex" alignItems="center">
              <CircularProgress size={12} color="inherit" sx={{ mr: 1 }} />
              Loading steerage data...
            </MDTypography>
          )}
        </Grid>

        {/* Steerage Information */}
        {steerageData && (
          <Grid item xs={12}>
            <Card sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                Steerage Information
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MDInput
                    type="text"
                    label="Policy Number"
                    value={steerageData.policyNumber}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    type="text"
                    label="Authorization Code"
                    value={steerageData.authorizationCode}
                    fullWidth
                    disabled
                  />
                </Grid>
                {steerageData.providerAddress && (
                  <Grid item xs={12}>
                    <MDTypography variant="body2" color="text">
                      <strong>Provider Address:</strong> {steerageData.providerAddress}
                    </MDTypography>
                  </Grid>
                )}
                {steerageData.providerPhone && (
                  <Grid item xs={12}>
                    <MDTypography variant="body2" color="text">
                      <strong>Provider Phone:</strong> {steerageData.providerPhone}
                    </MDTypography>
                  </Grid>
                )}
                {steerageData.providerType && (
                  <Grid item xs={12}>
                    <MDTypography variant="body2" color="text">
                      <strong>Provider Type:</strong> {steerageData.providerType}
                    </MDTypography>
                  </Grid>
                )}
                {/* {steerageData.specialClaimDetails && (
                  <Grid item xs={12}>
                    <MDTypography variant="body2" color="text">
                      <strong>Special Details:</strong> {steerageData.specialClaimDetails}
                    </MDTypography>
                  </Grid>
                )} */}
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Provider Name */}
        <Grid item xs={12} md={6}>
          <MDInput
            type="text"
            label="Provider Name"
            name="providerName"
            value={formData.providerName}
            onChange={handleChange}
            fullWidth
            disabled={loading || success}
          />
        </Grid>

        {/* Contact Method Selection */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Contact Method</FormLabel>
            <RadioGroup
              row
              name="contactMethod"
              value={contactMethod}
              onChange={handleContactMethodChange}
            >
              <FormControlLabel 
                value="email" 
                control={<Radio />} 
                label="Email" 
                disabled={loading || success}
              />
              <FormControlLabel 
                value="fax" 
                control={<Radio />} 
                label="Fax" 
                disabled={loading || success}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Provider Email or Fax Number */}
        <Grid item xs={12} md={6}>
          {contactMethod === 'email' ? (
            <>
              <MDInput
                type="email"
                label="Provider Email"
                name="providerEmail"
                value={formData.providerEmail || ''}
                onChange={handleChange}
                error={errors.providerEmail}
                fullWidth
                required
                disabled={loading || success}
              />
              {errors.providerEmail && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  Valid Provider Email is required
                </MDTypography>
              )}
            </>
          ) : (
            <>
              <MDInput
                type="text"
                label="Provider Fax Number"
                name="faxNumber"
                value={formData.faxNumber || ''}
                onChange={handleChange}
                error={errors.faxNumber}
                fullWidth
                required
                disabled={loading || success}
                helperText="Must start with +1 (US fax numbers only)"
              />
              {errors.faxNumber && (
                <MDTypography variant="caption" color="error" fontWeight="light">
                  Valid US Fax Number is required (must start with +1)
                </MDTypography>
              )}
            </>
          )}
        </Grid>

        {/* Comments */}
        <Grid item xs={12}>
          <MDInput
            type="text"
            label="Comments"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            multiline
            rows={4}
            fullWidth
            disabled={loading || success}
          />
        </Grid>

        {/* Error Message */}
        {error && (
          <Grid item xs={12}>
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
          </Grid>
        )}

        {/* Success Message */}
        {successMessage && (
          <Grid item xs={12}>
            <MDTypography variant="body2" color="success">
              {successMessage}
            </MDTypography>
          </Grid>
        )}

        {/* Buttons */}
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          {!success ? (
            <MDButton
              type="submit"
              variant="contained"
              color="info"
              disabled={loading || !isFormValid()}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Sending...
                </>
              ) : (
                'Send VOB'
              )}
            </MDButton>
          ) : (
            <MDButton
              variant="contained"
              color="success"
              onClick={handleNewVOB}
            >
              New VOB
            </MDButton>
          )}
        </Grid>
      </Grid>
      <VobAuthCodeList />
    </MDBox>
  );
};

export default VOBForm;
